"use client";

import { useEffect } from "react";
import i18n from "i18next";
import { initReactI18next, I18nextProvider } from "react-i18next";
import { i18nConfig } from "./i18n-config";
import en from "@/i18n/en.json";
import fr from "@/i18n/fr.json";

const resources = {
  en: { common: en as Record<string, unknown> },
  fr: { common: fr as Record<string, unknown> },
};

function getBrowserLanguage(): string {
  if (typeof navigator === "undefined") return i18nConfig.defaultLocale;
  const browser =
    navigator.language || (navigator.languages && navigator.languages[0]) || "";
  const match = i18nConfig.locales.find((locale) =>
    browser.toLowerCase().startsWith(locale)
  );
  return match ?? i18nConfig.defaultLocale;
}

function createI18n() {
  const instance = i18n.createInstance();
  instance
    .use(initReactI18next)
    .init({
      resources,
      lng: i18nConfig.defaultLocale,
      fallbackLng: i18nConfig.defaultLocale,
      defaultNS: i18nConfig.defaultNS,
      ns: [...i18nConfig.ns],
      returnObjects: i18nConfig.returnObjects,
      interpolation: i18nConfig.interpolation,
      react: {
        useSuspense: false,
      },
    });
  return instance;
}

let i18nInstance: ReturnType<typeof createI18n> | null = null;

function getI18n() {
  if (typeof window === "undefined") {
    return createI18n();
  }
  if (!i18nInstance) {
    i18nInstance = createI18n();
  }
  return i18nInstance;
}

export function I18nProvider({ children }: { children: React.ReactNode }) {
  const i18n = getI18n();

  useEffect(() => {
    const preferred = getBrowserLanguage();
    if (i18n.language !== preferred) {
      i18n.changeLanguage(preferred);
    }
  }, [i18n]);

  return <I18nextProvider i18n={i18n}>{children}</I18nextProvider>;
}
